import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { Splide, SplideSlide, SplideProps } from '@splidejs/react-splide';

import { gettext, interpolate } from '../../utils/text';

export type AccessibleCarouselSlide = {
    id?: string | number;
    content: React.ReactNode;
    ariaLabel?: string;
    className?: string;
    style?: React.CSSProperties;
};

type AccessibleCarouselProps = {
    slides: AccessibleCarouselSlide[];
    ariaLabel?: string;
    className?: string;
    variant?: 'default' | 'modal';
    showDots?: boolean;
    showControls?: boolean;
    initialIndex?: number;
    splideOptions?: SplideProps['options'];
    onSlideChange?: (index: number) => void;
    ariaAnnouncements?: boolean;
};

const AccessibleCarousel: React.FC<AccessibleCarouselProps> = ({
    slides,
    ariaLabel,
    className,
    variant = 'default',
    showDots = true,
    showControls = true,
    initialIndex = 0,
    splideOptions,
    onSlideChange,
    ariaAnnouncements = true,
}) => {
    const totalSlides = slides.length;
    const clampedInitial = Math.min(initialIndex, Math.max(totalSlides - 1, 0));
    const label = ariaLabel || gettext('Carousel');
    const [liveMessage, setLiveMessage] = useState('');

    const splideClassName = clsx('accessible-carousel__splide', {
        'accessible-carousel__splide--modal': variant === 'modal',
    });

    const options: SplideProps['options'] = useMemo(
        () => ({
            type: 'slide',
            pagination: showDots,
            arrows: showControls,
            start: clampedInitial,
            accessibility: true,
            keyboard: 'global',
            rewind: totalSlides > 1,
            live: false,
            ...splideOptions,
        }),
        [clampedInitial, showControls, showDots, splideOptions, totalSlides],
    );

    const buildLiveMessage = useCallback(
        (index: number) => {
            const slideLabel = slides[index]?.ariaLabel;
            return slideLabel
                ? `${slideLabel} (${index + 1} of ${totalSlides})`
                : interpolate(
                      gettext('%(label)s slide %(current)s of %(total)s'),
                      {
                          label,
                          current: index + 1,
                          total: totalSlides,
                      },
                      true,
                  );
        },
        [label, slides, totalSlides],
    );

    useEffect(() => {
        if (totalSlides) {
            setLiveMessage(buildLiveMessage(clampedInitial));
        }
    }, [buildLiveMessage, clampedInitial, totalSlides]);

    if (!totalSlides) {
        return null;
    }

    const componentClassName = clsx(
        'accessible-carousel',
        `accessible-carousel--${variant}`,
        className,
    );

    return (
        <section className={componentClassName} aria-label={label}>
            {ariaAnnouncements && (
                <div
                    className="visually-hidden"
                    aria-live="polite"
                    aria-atomic="true"
                >
                    {liveMessage}
                </div>
            )}
            <Splide
                key={`accessible-carousel-${totalSlides}`}
                className={splideClassName}
                options={options}
                aria-label={label}
                onMoved={(splide, index) => {
                    setLiveMessage(buildLiveMessage(index));
                    onSlideChange?.(index);
                }}
                onMounted={splide => {
                    setLiveMessage(buildLiveMessage(splide.index));
                    onSlideChange?.(splide.index);
                }}
            >
                {slides.map((slide, index) => (
                    <SplideSlide
                        key={slide.id || index}
                        className={clsx(
                            'accessible-carousel__slide',
                            slide.className,
                        )}
                        style={slide.style}
                        aria-label={slide.ariaLabel}
                    >
                        {slide.content}
                    </SplideSlide>
                ))}
            </Splide>
        </section>
    );
};

export default AccessibleCarousel;
